import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import { ExistingCompany, FormContactField, FormContactScreen } from './types'

type Props = {
  step: FormContactScreen
  stepNumber: number
  totalSteps: number
  isFirstStep: boolean
  isLastStep: boolean
  values: Record<string, string>
  existingCompanies: ExistingCompany[]
  onChange: (key: string, value: string) => void
  onNext: () => void
  onPrevious: () => void
  onBackToHomepage: () => void
  selectedUserName: string
  isSubmitting: boolean
}

const MAX_COMPANY_SUGGESTIONS = 6

// Autocomplete input for the `company` contact field. Filters the
// preloaded list of companies already used in this event by
// case-insensitive substring as the user types, surfacing the most-used
// canonical spelling first so leads from the same company end up grouped
// instead of fragmenting into typo'd duplicates. Selecting a suggestion
// fills the input but leaves it editable — exactly like a Google search
// box. If no matches, the dropdown hides and the user just keeps typing.
function CompanyAutocompleteInput({
  field,
  value,
  onChange,
  existingCompanies,
}: {
  field: FormContactField
  value: string
  onChange: (value: string) => void
  existingCompanies: ExistingCompany[]
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close the dropdown when the user clicks outside the input/menu.
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const trimmed = value.trim().toLowerCase()
  const suggestions =
    trimmed.length >= 1
      ? existingCompanies
          .filter((c) => c.name.toLowerCase().includes(trimmed) && c.name.toLowerCase() !== trimmed)
          .slice(0, MAX_COMPANY_SUGGESTIONS)
      : []
  const showDropdown = open && suggestions.length > 0

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        required={field.required}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
        className="h-[60px] w-full rounded-md border border-[#d1d5db] bg-white px-3 py-2 font-body text-lg shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
      />
      {showDropdown && (
        <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-[280px] overflow-y-auto rounded-md border border-[#d1d5db] bg-white shadow-lg">
          {suggestions.map((c) => (
            <li key={c.name}>
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur before our click handler fires.
                  e.preventDefault()
                  onChange(c.name)
                  setOpen(false)
                }}
                className="flex w-full px-4 py-3 text-left font-body text-base text-[#0c0a08] hover:bg-[#f3f4f6]"
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Contact step. Renders all `contact_fields` (system + non-system) as
// plain inputs driven by field_type. Required fields gate the Next/Submit
// button. Position-aware: if this is the last step, the button reads
// "Submit"; otherwise "Next".
export default function ContactStep({
  step,
  stepNumber,
  totalSteps,
  isFirstStep,
  isLastStep,
  values,
  existingCompanies,
  onChange,
  onNext,
  onPrevious,
  onBackToHomepage,
  selectedUserName,
  isSubmitting,
}: Props) {
  const canAdvance = step.fields.every(
    (f) => !f.required || (values[f.key] && values[f.key].trim().length > 0),
  )
  const nextLabel = isLastStep ? (isSubmitting ? 'Submitting…' : 'Submit') : 'Next'

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (canAdvance && !isSubmitting) onNext()
  }

  return (
    <div className="min-h-screen w-full bg-[#f9fafb] text-[#0c0a08]">
      <div className="relative mx-auto flex min-h-screen w-full max-w-[800px] flex-col">
        <header className="flex items-start justify-between px-12 pt-5 pb-10">
          <button
            type="button"
            onClick={onBackToHomepage}
            className="rounded-md bg-[#e5e7eb] px-4 py-3 text-sm font-semibold text-[#0c0a08]/60 transition hover:bg-[#d1d5db]"
          >
            Back to Homepage
          </button>
          <div className="rounded-md border border-[#d1d5db] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-black">
            {selectedUserName}
          </div>
        </header>

        <div className="flex gap-2 px-12">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
            <div key={n} className="flex-1">
              <div className={`h-1 w-full ${n <= stepNumber ? 'bg-[#004f51]' : 'bg-[#d1d5db]'}`} />
              {n === stepNumber && (
                <p className="pt-4 font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#004f51]">
                  Step {n}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="px-12 pt-5">
          <h1 className="font-display text-5xl leading-[1.15] text-[#004f51]">{step.text}</h1>
          {step.description && (
            <p className="mt-5 font-body text-lg text-[#111d1e]">{step.description}</p>
          )}
        </div>

        <form className="flex flex-1 flex-col gap-12 px-12 py-12" onSubmit={handleFormSubmit}>
          <div className="flex flex-col gap-6">
            {step.fields.map((f) => (
              <label key={f.key} className="flex flex-col gap-1">
                <span className="font-body text-lg text-[#111d1e]">
                  {f.label}
                  {f.required && <span className="ml-1 text-red-600">*</span>}
                </span>
                {f.key === 'company' ? (
                  <CompanyAutocompleteInput
                    field={f}
                    value={values[f.key] || ''}
                    onChange={(v) => onChange(f.key, v)}
                    existingCompanies={existingCompanies}
                  />
                ) : (
                  <input
                    name={f.key}
                    type={f.field_type === 'number' ? 'number' : f.field_type}
                    required={f.required}
                    value={values[f.key] || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(f.key, e.target.value)}
                    className="h-[60px] rounded-md border border-[#d1d5db] bg-white px-3 py-2 font-body text-lg shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
                  />
                )}
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            {!isFirstStep && (
              <button
                type="button"
                onClick={onPrevious}
                className="rounded-md border border-[#0c0a08]/40 bg-white px-8 py-4 font-display text-xl font-semibold text-[#111d1e]"
              >
                Previous
              </button>
            )}
            <button
              type="submit"
              disabled={!canAdvance || isSubmitting}
              className="flex items-center gap-2 rounded-md bg-[#111d1e] px-8 py-4 font-display text-xl font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-black"
            >
              {nextLabel}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
